/**
 * 跨 Tab 认证通信 Hook
 * 用于处理新 Tab 登录成功后通知原 Tab 的场景
 */
import { useEffect, useCallback, useRef } from 'react';

/** 认证事件类型 */
export type AuthEventType = 'AUTH_SUCCESS' | 'AUTH_LOGOUT';

/** 认证事件数据 */
export interface AuthEvent {
  type: AuthEventType;
  timestamp: number;
}

/** BroadcastChannel 名称 */
const AUTH_CHANNEL_NAME = 'easesourcing_auth_channel';

/** localStorage 事件 key（用于兼容不支持 BroadcastChannel 的浏览器） */
const AUTH_STORAGE_KEY = 'easesourcing_auth_event';

/**
 * 跨 Tab 认证通信 Hook
 * @param onAuthSuccess 认证成功回调
 * @param onAuthLogout 登出回调（可选）
 */
export function useAuthChannel(onAuthSuccess?: () => void, onAuthLogout?: () => void) {
  const channelRef = useRef<BroadcastChannel | null>(null);

  // 初始化 BroadcastChannel
  useEffect(() => {
    // 检查浏览器是否支持 BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel(AUTH_CHANNEL_NAME);

      // 监听消息
      channelRef.current.onmessage = (event: MessageEvent<AuthEvent>) => {
        const { type } = event.data;
        if (type === 'AUTH_SUCCESS') {
          onAuthSuccess?.();
        } else if (type === 'AUTH_LOGOUT') {
          onAuthLogout?.();
        }
      };
    }

    // 监听 localStorage 变化（兼容方案）
    const handleStorage = (e: StorageEvent) => {
      if (e.key === AUTH_STORAGE_KEY && e.newValue) {
        try {
          const event: AuthEvent = JSON.parse(e.newValue);
          if (event.type === 'AUTH_SUCCESS') {
            onAuthSuccess?.();
          } else if (event.type === 'AUTH_LOGOUT') {
            onAuthLogout?.();
          }
        } catch {
          // 忽略解析错误
        }
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
      window.removeEventListener('storage', handleStorage);
    };
  }, [onAuthSuccess, onAuthLogout]);

  /**
   * 广播认证成功事件
   */
  const broadcastAuthSuccess = useCallback(() => {
    const event: AuthEvent = {
      type: 'AUTH_SUCCESS',
      timestamp: Date.now(),
    };

    // 通过 BroadcastChannel 广播
    channelRef.current?.postMessage(event);

    // 通过 localStorage 广播（兼容方案）
    // 先设置再清除，确保触发 storage 事件
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(event));
    // 延迟清除，确保其他 tab 能接收到
    setTimeout(() => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }, 100);
  }, []);

  /**
   * 广播登出事件
   */
  const broadcastAuthLogout = useCallback(() => {
    const event: AuthEvent = {
      type: 'AUTH_LOGOUT',
      timestamp: Date.now(),
    };

    channelRef.current?.postMessage(event);

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(event));
    setTimeout(() => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }, 100);
  }, []);

  return {
    broadcastAuthSuccess,
    broadcastAuthLogout,
  };
}

/**
 * 非 Hook 版本的广播方法
 * 用于在非组件上下文中调用（如 HTTP 拦截器）
 */
export const authChannel = {
  /**
   * 广播认证成功事件
   */
  broadcastAuthSuccess() {
    const event: AuthEvent = {
      type: 'AUTH_SUCCESS',
      timestamp: Date.now(),
    };

    // 通过 BroadcastChannel 广播
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      channel.postMessage(event);
      channel.close();
    }

    // 通过 localStorage 广播
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(event));
    setTimeout(() => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }, 100);
  },

  /**
   * 广播登出事件
   */
  broadcastAuthLogout() {
    const event: AuthEvent = {
      type: 'AUTH_LOGOUT',
      timestamp: Date.now(),
    };

    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      channel.postMessage(event);
      channel.close();
    }

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(event));
    setTimeout(() => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }, 100);
  },
};

export default useAuthChannel;
